import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import FilterSelector from "@components/filterSelector/FilterSelector";
import { useCallback, useEffect, useState } from "react";
import ViewSelector from "@components/viewSelector/viewSelector";
import Button from "@components/button/Button";
import { useDocument } from "@hooks/useDocument";
import { prepopulateFilterFromDefaultValues, generateFilterQueryParams } from "@utils/filtering";
import useDetectCurrentCulture from "@hooks/useDetectCurrentCulture";
import { SearchResultsFilters } from "./SearchResultsFilter";
import { SearchFilterSidebar } from "./SearchFilterSidebar";
import { SearchSortSidebar } from "./SearchSortSidebar";
import { SearchField } from "./SearchField";
import useTranslations from "@hooks/useTranslations";
import { Pagination } from "@components/pagination/Pagination";
import { useWindow } from "@hooks/useWindow";
import { usePaginatedApi } from "@hooks/useAPI";
import { mockRandomArr } from "@utils/helpers";
import FeaturedCardSkeleton from "@components/featured/FeaturedCardSkeleton";
import { SearchResultsList } from "./SearchResultsList";
import cn from "classnames";
const SearchResults = (props) => {
    const { filters, sortBy, initialSearchText } = props;
    const myWindow = useWindow();
    const myDocument = useDocument();
    const { t } = useTranslations();
    const { culture } = useDetectCurrentCulture();
    const [results, setResults] = useState([]);
    const [view, setView] = useState("grid");
    const [totalCount, setTotalCount] = useState(0);
    const [sortList, setSortList] = useState(sortBy);
    const [filterList, setFilterList] = useState(filters);
    const [selected, setSelected] = useState({
        currentPage: 1,
        pageSize: 12,
        searchText: initialSearchText,
        filter: null,
        sort: prepopulateFilterFromDefaultValues([sortList])
    });
    const [shouldShow, setShouldShow] = useState({
        results: false,
        filters: true,
        filtersMenu: false,
        sortMenu: false
    });
    const [resetPagination, setResetPagination] = useState(false);
    const { request, loading: isDataLoading } = usePaginatedApi(`api/${culture}/search`, {
        initialPage: 1,
        pageSize: 12
    });
    const activeFilters = !!selected.filter ? Object.values(selected.filter) : null;
    const submit = (searchText) => {
        toggleShow("results", false);
        setResetPagination(true);
        setSelected((prevState) => ({ ...prevState, searchText }));
    };
    /**
     * Toggle show state when `show` isn't provided.
     * otherwise, set show state to the provided `show` value explicitly.
     */
    const toggleShow = (state, show) => {
        return setShouldShow((prevState) => ({ ...prevState, [state]: show }));
    };
    const toggleFiltersMenu = (show) => {
        toggleShow("filtersMenu", show);
        toggleBodyClass();
    };
    const toggleSortMenu = (show) => {
        toggleShow("sortMenu", show);
        toggleBodyClass();
    };
    const toggleBodyClass = useCallback(() => {
        const classname = "modal-open";
        if (myDocument) {
            const bodyClass = myDocument.body.classList;
            if (bodyClass && Array.from(bodyClass).includes(classname)) {
                myDocument.body.classList.remove(classname);
            }
            else {
                myDocument.body.classList.add(classname);
            }
        }
    }, [myDocument]);
    const updateSortList = (sortValue) => {
        const sortListCopy = { ...sortList };
        sortListCopy.defaultValue = sortValue[sortListCopy.name];
        setSortList(sortListCopy);
    };
    const handleSortChange = (value) => {
        const selectedSort = generateFilterQueryParams(value);
        // set active sort for api params
        setSelected((prevState) => ({ ...prevState, sort: selectedSort }));
        // update sort list as one source of truth
        // for multiple sort components based on selected sort value
        updateSortList(selectedSort);
        setResetPagination(true);
    };
    const handleFilterChange = (value) => {
        const selectedFilter = generateFilterQueryParams(value);
        setSelected((prevState) => ({ ...prevState, filter: selectedFilter }));
        setResetPagination(true);
    };
    const handlePageChange = useCallback((page) => {
        if (!!myWindow) {
            setResetPagination(false);
            myWindow.scrollTo({ top: 0, behavior: "smooth" });
            setSelected((prevState) => ({ ...prevState, currentPage: page }));
        }
    }, [myWindow]);
    const clearFilters = () => {
        setSelected((prevState) => ({ ...prevState, filter: null }));
    };
    const search = async () => {
        var _a;
        try {
            const { searchText, filter, sort, currentPage, pageSize } = selected;
            const params = {
                currentPage,
                pageSize,
                searchText,
                ...filter,
                ...sort
            };
            const response = (await request({ params }));
            setResults(response.data);
            setTotalCount(response.totalCount);
            setFilterList((_a = response["filters"]) !== null && _a !== void 0 ? _a : []);
        }
        catch (error) {
            console.error(error);
        }
    };
    useEffect(() => {
        const hasValidParams = !!culture || Object.values(selected).some(Boolean);
        if (hasValidParams) {
            search();
        }
    }, [selected, culture]);
    return (_jsxs("div", { className: "search-results__wrapper", children: [_jsxs("div", { className: "search-results__search-field-container", children: [_jsx("div", { className: "search-results__search-field-filler" }), _jsx("div", { className: "search-results__search-field", children: _jsx(SearchField, { initialSearchText: selected.searchText, onToggle: (show) => toggleShow("results", show), shouldShowSuggestions: shouldShow.results, onSearch: submit }) })] }), _jsxs("div", { className: "search-results__sort-filter-wrapper", children: [_jsx("div", { className: "search-results__filter-container", children: _jsxs("div", { className: "search-results__filter", children: [_jsx(Button, { link: "#", type: "tertiaryWithIcon", className: "search-results__toggle-filter-btn", onClick: () => toggleShow("filters", !shouldShow.filters), title: !shouldShow.filters ? t("BDO.LiveSite.ShowFilters") : t("BDO.LiveSite.HideFilters"), icon: !shouldShow.filters ? "visibility" : "visibility_off" }), !!selected.filter && shouldShow.filters && (_jsx("button", { className: "search-results__clear-filter-btn", onClick: clearFilters, children: t("BDO.LiveSite.ClearFilters") }))] }) }), _jsx("div", { className: "search-results__sort-wrapper", children: _jsxs("div", { className: "search-results__sort-container", children: [_jsxs("div", { className: "search-results__sort-filter-wrapper--mobile", children: [_jsx(SearchFilterSidebar, { activeFilters: activeFilters, filters: filterList, isSideMenuVisible: shouldShow.filtersMenu, onClearFilters: clearFilters, onFilterClick: handleFilterChange, onToggle: toggleFiltersMenu }), _jsx(SearchSortSidebar, { activeSort: Object.values(selected.sort), sortList: sortList, isSideMenuVisible: shouldShow.sortMenu, onSortClick: (sort) => handleSortChange([sort]), onToggle: toggleSortMenu })] }), _jsx("div", { className: "search-results__results-count", children: !isDataLoading && (_jsxs("p", { children: [t("BDO.LiveSite.SearchPage.SearchResultsFound"), " ", totalCount] })) }), _jsxs("div", { className: "search-results__sort-view-container", children: [!!culture && _jsx(FilterSelector, { oneColLayout: true, initialFilters: [sortList], labelPlaceholder: t(sortList.title), onFiltersModified: handleSortChange }), _jsx(ViewSelector, { onClick: setView })] })] }) })] }), _jsxs("section", { className: "search-results__section", children: [shouldShow.filters && (_jsx("aside", { className: "search-results__results-filter", children: _jsx(SearchResultsFilters, { filters: filterList, activeFilters: activeFilters, onFilterClick: handleFilterChange }) })), _jsxs("section", { className: cn("search-results__results-wrapper", view === "list" && "mt-0"), children: [_jsxs("div", { className: cn(`search-results__container--${view}`), children: [isDataLoading && mockRandomArr(8).map((item) => _jsx(FeaturedCardSkeleton, {}, item)), !isDataLoading && !!results.length && _jsx(SearchResultsList, { view: view, results: results })] }), _jsx(Pagination, { className: !!!results.length && "hidden", totalCount: totalCount, pageSize: selected.pageSize, onPageChange: handlePageChange, shouldReset: resetPagination })] })] })] }));
};
export default SearchResults;
